import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import eventClientApi from "@/api-client/event-client";
import { getQueryKey } from "@key.ai/constants";
import { cleanObject } from "@/lib/helper";

// Types
export interface InvitedUser {
  id: string;
  name: string;
  email: string;
}

export interface InvitedUsersResponse {
  status: number;
  message: string;
  data: {
    count: number;
    data: InvitedUser[];
  };
}

interface UseInvitedUsersProps {
  eventId: string;
  limit?: number;
  payload?: any;
  search: string;
  enabled?: boolean;
}

export const useInvitedUsers = ({
  eventId,
  limit = 15,
  payload,
  search,
  enabled
}: UseInvitedUsersProps) => {
  const queryKey = getQueryKey("EVENT_INVITED_USERS");

  const fetchInvitedUsers = async ({ pageParam = 1 }: { pageParam?: number }) => {
    const { data } = await eventClientApi
      .post(`api/v1/events/${eventId}/invite/users`, {
        searchParams: cleanObject({
          page: pageParam.toString(),
          limit: limit.toString(),
          search: search
        }),
        json: payload
      })
      .json<InvitedUsersResponse>();

    return {
      data: data.data,
      count: data.count,
      pageParam
    };
  };

  return useInfiniteQuery({
    queryKey: [queryKey, eventId, search, JSON.stringify(payload)],
    queryFn: fetchInvitedUsers,
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.count / limit);
      return lastPage.pageParam < totalPages ? lastPage.pageParam + 1 : undefined;
    },
    initialPageParam: 1,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    enabled: !!enabled
  });
};
